import { combineEpics, ofType } from 'redux-observable'
import { forkJoin, of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { addToast } from 'containers/Toasts/actions'

import {
  deleteMalwareDetectionError,
  deleteMalwareDetectionSuccess,
  getMalwareDetectionsError,
  getMalwareDetectionsSuccess,
  updateMalwareDetectionError,
  updateMalwareDetectionSuccess,
} from './actions'
import {
  DELETE_MALWARE_DETECTION_ASYNC,
  GET_MALWARE_DETECTIONS_ASYNC,
  UPDATE_MALWARE_DETECTION_ASYNC,
} from './constants'

const malwareDetectionsKeys = ['opswat', 'vxstream', 'paloalto', 'gradient', 'connectwise-psa']

const getMalwareDetectionsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      GET_MALWARE_DETECTIONS_ASYNC.INITIATE,
      UPDATE_MALWARE_DETECTION_ASYNC.SUCCESS,
      DELETE_MALWARE_DETECTION_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.malware)
      return true
    }),
    mergeMap(() =>
      forkJoin(
        malwareDetectionsKeys.map((key) =>
          api.settings
            .getMalwareDetection(state$.value.partnerOptions.selectedCompany?.value, key)
            .pipe(
              map((response) => getMalwareDetectionsSuccess({ name: key, data: response })),
              catchError((error) => of(epicsErrorHandler(getMalwareDetectionsError, error, [400]))),
              takeUntil(action$.pipe(ofType(GET_MALWARE_DETECTIONS_ASYNC.CANCEL)))
            )
        )
      )
    ),
    mergeMap((actions) => actions)
  )

const updateMalwareDetectionEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_MALWARE_DETECTION_ASYNC.INITIATE),
    mergeMap((action) =>
      api.settings
        .updateMalwareDetection(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.name,
          action.payload.data
        )
        .pipe(
          mergeMap((response) => {
            window.closeModal()
            return of(
              updateMalwareDetectionSuccess(response),
              addToast({ message: 'Successfully integrated!' })
            )
          }),
          catchError((error) => of(epicsErrorHandler(updateMalwareDetectionError, error))),
          takeUntil(action$.pipe(ofType(UPDATE_MALWARE_DETECTION_ASYNC.CANCEL)))
        )
    )
  )

const deleteMalwareDetectionEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_MALWARE_DETECTION_ASYNC.INITIATE),
    mergeMap((action) => {
      const { name } = action.payload

      if (name === 'connectwise') {
        return api.connectWisePSA.deactivateIntegration().pipe(
          mergeMap((response) => {
            // Extract response data (rxfetch post returns {response: {...}})
            const data = response.response || response

            return of(
              deleteMalwareDetectionSuccess(data),
              addToast({
                message: data.message || 'ConnectWise integration deactivated successfully!',
              }), // Trigger refresh of malware detections to update the status indicator
              { type: GET_MALWARE_DETECTIONS_ASYNC.INITIATE }
            )
          }),
          catchError((error) => of(epicsErrorHandler(deleteMalwareDetectionError, error))),
          takeUntil(action$.pipe(ofType(DELETE_MALWARE_DETECTION_ASYNC.CANCEL)))
        )
      }

      return api.settings
        .deleteMalwareDetection(state$.value.partnerOptions.selectedCompany?.value, name)
        .pipe(
          mergeMap((response) =>
            of(
              deleteMalwareDetectionSuccess(response),
              // Trigger refresh of malware detections to update the status indicator
              { type: GET_MALWARE_DETECTIONS_ASYNC.INITIATE }
            )
          ),
          catchError((error) => of(epicsErrorHandler(deleteMalwareDetectionError, error))),
          takeUntil(action$.pipe(ofType(DELETE_MALWARE_DETECTION_ASYNC.CANCEL)))
        )
    })
  )

// connectWiseEpics is NOT combined here: its slice (`connectWise`,
// reducer.js:48) is static, so a route-scoped `Settings` barrel would strand
// it mid-flight on eject. Extracted and registered as the always-on
// `ConnectWise` registry key in src/epics.js instead.
export default combineEpics(
  getMalwareDetectionsEpic,
  updateMalwareDetectionEpic,
  deleteMalwareDetectionEpic
)
